/**
 * ClientLogo — single source of truth for rendering a client's logo
 * (or initials fallback) anywhere in the app.
 *
 * Mirrors UserAvatar.js's photo→initials fallback pattern (img with
 * onError → broken state → deterministic-colour initials box) so client
 * logos behave consistently with how user avatars already work, instead
 * of every call site rolling its own broken-image-prone <img> tag.
 *
 * 2026-07 client-logo repair sprint: the migration left many
 * `clients.logo_url` values pointing at Azure Blob Storage objects that
 * no longer exist. Those 404 silently as a raw browser broken-image icon
 * unless the caller adds onError handling — this component makes that
 * the default everywhere a client logo is shown, and is landscape-boxed
 * (object-contain, not object-cover) since company logos are typically
 * wide rather than square.
 */
import { useState } from "react";
import { resolveBackendFileUrl } from "@/lib/fileUrls";

const SIZE_CLASSES = {
  xs: "w-6 h-6 text-[8px]",           // list-row icon slot (ClientsPage)
  sm: "w-[100px] h-[36px] text-[10px]", // compact inline usage
  lg: "w-[240px] h-[80px] text-sm",     // client detail / dashboard hero box
};

// Same stable palette as UserAvatar so the two fallback styles feel like
// one system rather than two different color schemes.
const COLOR_PAIRS = [
  ["bg-[#F0F7FF]", "text-[#0055FF]"],
  ["bg-[#FEF3C7]", "text-[#92400E]"],
  ["bg-[#F0FDF4]", "text-[#16A34A]"],
  ["bg-[#FCE7F3]", "text-[#BE185D]"],
  ["bg-[#FFE4E6]", "text-[#BE123C]"],
  ["bg-[#EDE9FE]", "text-[#6D28D9]"],
  ["bg-[#E0F2FE]", "text-[#0369A1]"],
  ["bg-[#FFF7ED]", "text-[#C2410C]"],
];

function pickColor(seed) {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return COLOR_PAIRS[Math.abs(hash) % COLOR_PAIRS.length];
}

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map(p => p.charAt(0).toUpperCase()).join("") || "?";
}

export default function ClientLogo({
  client,
  name,
  logoUrl,
  size = "sm",
  className = "",
  testId,
}) {
  const [broken, setBroken] = useState(false);

  const resolvedName = client?.name ?? name ?? "";
  const resolvedLogo = resolveBackendFileUrl(client?.logo_url ?? logoUrl ?? "");
  const sizeCls = SIZE_CLASSES[size] || SIZE_CLASSES.sm;
  const seed = resolvedName || (client?.id || "?");
  const [bg, fg] = pickColor(seed);
  const initials = getInitials(resolvedName);
  const baseCls = `${sizeCls} flex-shrink-0 inline-flex items-center justify-center font-bold ${className}`;

  if (resolvedLogo && !broken) {
    return (
      <img
        src={resolvedLogo}
        alt={`${resolvedName || "Client"} logo`}
        title={resolvedName || undefined}
        className={`${baseCls} object-contain bg-white`}
        onError={() => setBroken(true)}
        data-testid={testId}
      />
    );
  }

  return (
    <div
      className={`${baseCls} ${bg} ${fg}`}
      title={resolvedName || undefined}
      data-testid={testId}
    >
      {initials}
    </div>
  );
}
